import { DataAction, Payload, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { EventEmitter, Inject, Injectable, Injector } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Selector, State, Store } from '@ngxs/store';
import produce from 'immer';
import { combineLatest, Subscription } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { ITaskDialogMetadata } from 'src/app/components/task-dialog/task-dialog.models';
import { ILazyComponentHandler } from 'src/app/models/smart-actions.models';
import { MemberService } from 'src/app/services/member/member.service';
import { TaskOrchestratorEndpoint } from 'src/app/services/task-orchestrator/task-orchestrator-endpoint.service.ts';
import { TaskService, TaskServiceFactory, TaskServiceFactoryFn } from 'src/app/services/task/task.service';
import { IInterviewDataProvider } from 'src/app/shared/interview-new/instances/data-providers/data-providers.models';
import { ITaskInterviewMetadata } from 'src/app/shared/interview-new/instances/task-adapter-interview/task-adapter-interview.models';
import { AssessmentMode, EvaluationSeverityMode, ExecutionMode, IInterviewEvent, IInterviewSettings, InterviewEventType } from 'src/app/shared/interview-new/models/interview-handler.models';
import { InterviewHandlerService } from 'src/app/shared/interview-new/services/interview-handler.service';
import { ILazyLoadContext } from 'src/app/shared/lazy-loader/lazy-loader.models';
import { IOrchestratorTask, ITaskOrchestratorContext, ITaskOrchestratorStateModel, OrchestratorTask } from '../../pages/member/+components/task-orchestrator/task-orchestrator.models';
import { MemberNavigationService } from '../../pages/member/member.navigation.service';
import { CommandParserService } from '../task/command/command-parser.service';
import { FeatureService } from 'src/app/shared/preview-feature/preview-feature.service';

@StateRepository()
@State<ITaskOrchestratorStateModel>({
    name: 'memberTaskIntegratorState',
    defaults: {
        currentTaskId: null,
        context: null
    }
})
@Injectable({
    providedIn: 'root'
})
export class TaskOrchestratorService extends NgxsDataRepository<ITaskOrchestratorStateModel> implements ILazyComponentHandler {

    static runTaskParamName = 'run';

    showDialog: boolean;
    mode: 'task' | 'review';

    taskService: TaskService;

    // interviews
    interviewHandler = new InterviewHandlerService();
    interviewHandlerSettings: IInterviewSettings;
    interviewHandlerEvents$$: Subscription;

    tempMockedContextNumber: number;
    forceNavigation$ = new EventEmitter<any>();
    processingContext: boolean;

    lazyComponentHandlerId?: string = 'task-orchestrator-dialog';
    currentCustomViewMetadata?: any;
    customViewSync: string;
    communityFeature: boolean;
    activeFeatures: any;

    @Selector()
    static contextSelector(state: ITaskOrchestratorStateModel): ITaskOrchestratorContext {
        return state.context;
    }


    @Selector([TaskOrchestratorService.contextSelector])
    static tasksSelector(state: ITaskOrchestratorStateModel, context: ITaskOrchestratorContext): OrchestratorTask[] {
        return context?.tasks?.map(task => new OrchestratorTask(task));
    }

    @Selector([TaskOrchestratorService.contextSelector])
    static contextTitleSelector(state: ITaskOrchestratorStateModel, context: ITaskOrchestratorContext): string {
        return context?.title;
    }

    @Selector()
    static currentTaskIdSelector(state: ITaskOrchestratorStateModel): string {
        return state.currentTaskId;
    }


    @Selector()
    static nextTaskIdSelector(state: ITaskOrchestratorStateModel): string {
        return state.nextTaskId;
    }

    @Selector([TaskOrchestratorService.tasksSelector, TaskOrchestratorService.currentTaskIdSelector])
    static currentTaskSelector(state: ITaskOrchestratorStateModel, tasks: IOrchestratorTask[], id: string): IOrchestratorTask {
        return tasks.find(task => task.id == id);
    }

    @Selector([TaskOrchestratorService.tasksSelector, TaskOrchestratorService.nextTaskIdSelector])
    static nextTaskSelector(state: ITaskOrchestratorStateModel, tasks: IOrchestratorTask[], id: string): IOrchestratorTask {
        return tasks.find(task => task.id == id);
    }

    get contextSync() {
        return this.store.selectSnapshot(TaskOrchestratorService.contextSelector);

    }
    get currentTaskIdSync() {
        return this.store.selectSnapshot(TaskOrchestratorService.currentTaskIdSelector);
    }

    get nextTaskIdSync() {
        return this.store.selectSnapshot(TaskOrchestratorService.nextTaskIdSelector);
    }

    get currentTaskSync() {
        return this.store.selectSnapshot(TaskOrchestratorService.currentTaskSelector);
    }
    get nextTaskSync() {
        return this.store.selectSnapshot(TaskOrchestratorService.nextTaskSelector);
    }

    get tasksSync() {
        return this.store.selectSnapshot(TaskOrchestratorService.tasksSelector);
    }

    get tasks$() {
        return this.store.select(TaskOrchestratorService.tasksSelector);
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private store: Store,
        private injector: Injector,
        private memberService: MemberService,
        private commandParser: CommandParserService,
        private endpoint: TaskOrchestratorEndpoint,
        private readonly featureService: FeatureService,
        @Inject(TaskServiceFactory) taskService: TaskServiceFactoryFn

    ) {
        super();

        this.taskService = taskService().setContext();
        this.taskService.activate();

    }

    setCurrentCustomView(viewId: string, metadata?: any) {

        this.currentCustomViewMetadata = metadata;

        this.customViewSync = null;
        setTimeout(() => {
            this.customViewSync = viewId;
            this.selectNextTask();
        });
    }

    async ngxsAfterBootstrap() {
        super.ngxsAfterBootstrap();

        this.initializeRouteListener();
    }

    initializeRouteListener() {
        const memberContext$ = this.memberService.memberContext$.pipe(filter(_ => !!_))
        combineLatest([memberContext$, this.route.queryParams]).pipe(map(([context, params]) => params)).subscribe(async params => {

            if (this.memberService.memberTaskOrchestratorConfigSync) {
                await this.processMode(params);
            }
        });
    }

    private async loadContext(force = true) {

        try {
            this.processingContext = true;

            if (force || !this.contextSync) {

                const context = await this.getContext();

                this.setContext(context);
                this.tempMockedContextNumber = (this.tempMockedContextNumber || 0) + 1;

                this.processContext();
            }
        }
        finally {
            this.processingContext = false;
        }
    }
    private async getContext(mode?: string) {
        let payload = await this.memberService.getCurrentMemberRequestPayload().pipe(filter(_ => !!_), first()).toPromise();

        this.activeFeatures = this.featureService.activeFeatures();

        payload = { ...payload, ...this.memberService.memberTaskOrchestratorConfigSync, ...this.activeFeatures };
        (payload as unknown as any).mode = mode;

        const memberBase64 = this.memberService.base64Sync;

        return await this.endpoint.getTaskOrchestratorContext(memberBase64, payload);
    }

    private processContext() {

        if (this.currentTaskIdSync) {
            // selected task is not found in the list of tasks
            if (this.currentTaskSync == null) {
                const queryParams = {
                    [MemberNavigationService.displayModeParamName]: 'TASK_ORCHESTRATOR',
                    [TaskOrchestratorService.runTaskParamName]: undefined
                };
                this.forceNavigation$.next(queryParams);
            }
        } else {

        }

    }
    @DataAction()
    setContext(@Payload('context') context: ITaskOrchestratorContext) {

        this.ctx.setState(produce(this.ctx.getState(), (draft: ITaskOrchestratorStateModel) => {
            draft.context = context;
        }));
    }

    private async processMode(params: Params) {
        await this.loadContext(true);
        if (params[MemberNavigationService.displayModeParamName] == 'TASK_ORCHESTRATOR') {
            this.showDialog = true;
            const taskId = params[TaskOrchestratorService.runTaskParamName];
            switch (true) {
                case !!taskId:
                    this.mode = 'task';

                    // Don't reload if already loaded
                    if (taskId != this.currentTaskIdSync) {
                        await this.setCurrentTaskId(taskId);
                    }

                    break;
            }
        } else {
            this.showDialog = false;
            await this.setCurrentTaskId(undefined);
        }
    }

    @DataAction()
    async setCurrentTaskId(@Payload('taskId') taskId: string) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: ITaskOrchestratorStateModel) => {
            draft.currentTaskId = taskId;
        }));

        this.ctx.setState(produce(this.ctx.getState(), (draft: ITaskOrchestratorStateModel) => {
            draft.nextTaskId = this.retrieveNextTask()?.id;
        }));

        await this.showCurrentTask();
    }

    private async showCurrentTask() {
        if (this.currentTaskSync) {
            switch (this.currentTaskSync.type?.toUpperCase()) {
                case 'INTERVIEW':
                    await this.initializeInterview();
                    break;
                case 'COMPONENT':
                    await this.showComponent();
                    break;
            }
        }
    }

    private async showComponent() {

        const matchestComponentId = this.currentTaskSync.command.match(/[\s]*open[\s]*(?<componentId>[^\s]*)/i);

        // Cleanup up just in case we do not call another interview
        this.interviewHandler.clear();

        this.currentCustomViewMetadata = {
            ...this.commandParser.parseContextData(this.currentTaskSync.command),
            lazyComponentHandler: this
        };

        this.customViewSync = null;
        setTimeout(() => {

            this.customViewSync = matchestComponentId?.groups['componentId']?.replace(/"/g, '');
        });
    }

    private async initializeInterview(task?: IOrchestratorTask) {

        task ??= this.currentTaskSync;

        const { commandParse } = this.commandParser.parsedData(task.command);
        const parseData = commandParse as ILazyLoadContext<ITaskDialogMetadata<ITaskInterviewMetadata>>;
        const interviewData = parseData.customViewMetadata.customViewMetadata;
        const interviewDataProvider = interviewData.dataProvider ? this.injector.get(interviewData.dataProvider) as IInterviewDataProvider : null;
        const byPassToSummary = interviewData.byPassToSummary;
        this.interviewHandlerSettings = {
            type: interviewData.type || 'Eligibility',
            package: interviewData.package,
            version: interviewData.version,
            dataProvider: interviewDataProvider,
            summaryComponentId: interviewData.summaryComponent,
            mode: AssessmentMode.Primary,
            executionMode: ExecutionMode.Standard,
            evaluationSeverityMode: EvaluationSeverityMode.Strict,
            showInterviewMetadata: true,
            byPassToSummary: byPassToSummary,
            style: {
                zIndex: 1000002
            },

            contextDataCallback: (() => {

                const contextData = {
                    ...interviewData.contextData,
                    Member: this.memberService.memberSync,
                };

                if (interviewDataProvider != null) {
                    return interviewDataProvider?.generateContextData(contextData);
                }

                return contextData;

            }).bind(this)
        };

        if (this.interviewHandler) {
            if (this.interviewHandlerEvents$$) {
                try {
                    this.interviewHandlerEvents$$.unsubscribe();
                    this.interviewHandlerEvents$$ = null;
                } catch (ex) { }
            }

            this.interviewHandler.clear();
            this.interviewHandler = null;
        }


        this.interviewHandler = new InterviewHandlerService();
        this.interviewHandlerEvents$$ = this.interviewHandler.events.lifeCycleEvent$.subscribe(event => {
            this.handleTaskEvent(event);
        });

        this.interviewHandler.initialize(this.interviewHandlerSettings);
    }

    private async handleTaskEvent(event: IInterviewEvent) {
        switch (event.type) {
            case InterviewEventType.Done:
                await this.loadContext(true);
                this.selectNextTask();
                break;
            case InterviewEventType.Evaluation:
                break;
        }
    }
    private selectNextTask() {
        const nextTaskId = this.retrieveNextTask()?.id;

        const queryParams = {
            [MemberNavigationService.displayModeParamName]: 'TASK_ORCHESTRATOR',
            [TaskOrchestratorService.runTaskParamName]: nextTaskId
        };
        this.forceNavigation$.next(queryParams);
    }


    private retrieveNextTask() {
        let nextFromCurrentIndex = 0;
        if (this.currentTaskIdSync) {
            const currentIndex = this.tasksSync.findIndex(task => task.id == this.currentTaskIdSync);

            for (let i = currentIndex + 1; i < this.tasksSync.length; i++) {
                if (this.tasksSync[i].active) {
                    return this.tasksSync[i];
                }
            }
        }
    }
}
